/**
 * Compute shift score contributions from ddxShiftEdges
 * for a set of mapped shift factor IDs.
 *
 * Review status is tracked for confidence reporting.
 * Magnitude weight is the same for pending vs reviewed edges.
 * Suppression (-999 / suppressedBy) applies only when a negating or refuting
 * edge has status reviewed — pending AI edges never suppress.
 *
 * @param {string[]} factorIds
 * @param {(factorId: string) => Promise<object[]> | object[]} getShiftEdgesForFactor
 * @returns {Promise<Record<string, {
 *   shiftScore: number,
 *   edges: Array<{
 *     factorId: string,
 *     conditionId: string,
 *     shiftDirection: string,
 *     shiftMagnitude: number,
 *     clinicalRationale: string,
 *     sourceReference: string | null,
 *     sourceUrl: string | null,
 *     calculatorDerived: object | null,
 *     isReviewed: boolean,
 *   }>,
 *   suppressedBy: string | null,
 *   reviewedEdgeCount: number,
 *   pendingEdgeCount: number,
 * }>>}
 */
export async function computeShiftScores(factorIds, getShiftEdgesForFactor) {
  const conditionScores = {};
  const uniqueFactorIds = [...new Set((factorIds || []).filter(Boolean).map(String))];

  for (const factorId of uniqueFactorIds) {
    // eslint-disable-next-line no-await-in-loop
    const edges = await Promise.resolve(getShiftEdgesForFactor(factorId));
    for (const edge of edges || []) {
      if (!edge || edge.status === 'deprecated') continue;
      const conditionId = String(edge.conditionId || '').trim();
      if (!conditionId) continue;

      if (!conditionScores[conditionId]) {
        conditionScores[conditionId] = {
          shiftScore: 0,
          edges: [],
          suppressedBy: null,
          reviewedEdgeCount: 0,
          pendingEdgeCount: 0,
        };
      }
      const bucket = conditionScores[conditionId];

      const direction = String(edge.shiftDirection || '').trim();
      // Suppression (removal from candidates) requires a reviewed edge.
      // Pending refuting edges contribute to scoring only — they never suppress.
      // See: ddx-plus suppression safety fix, April 2026.
      const isSuppressing =
        (edge?.isNegating === true ||
          direction === 'weakly_refutes' ||
          direction === 'strongly_refutes') &&
        edge.status === 'reviewed';
      if (isSuppressing) {
        bucket.suppressedBy = factorId;
        bucket.shiftScore = -999;
        continue;
      }

      if (bucket.suppressedBy) continue;
      bucket.shiftScore += Number(edge.shiftMagnitude) || 0;

      if (edge.status === 'reviewed') bucket.reviewedEdgeCount += 1;
      else bucket.pendingEdgeCount += 1;

      bucket.edges.push({
        factorId,
        conditionId,
        shiftDirection: direction,
        shiftMagnitude: Number(edge.shiftMagnitude) || 0,
        clinicalRationale: String(edge.clinicalRationale || ''),
        sourceReference: edge.sourceReference ? String(edge.sourceReference) : null,
        sourceUrl: edge.sourceUrl ? String(edge.sourceUrl) : null,
        calculatorDerived: edge.calculatorDerived || null,
        isReviewed: edge.status === 'reviewed',
      });
    }
  }

  return conditionScores;
}

/**
 * Remap condition-scoped shift scores to diagnosis IDs and merge collisions.
 *
 * @param {Record<string, object>} conditionScores
 * @param {Record<string, string | null | undefined>} conditionToDiagnosisId
 * @returns {Record<string, {
 *   shiftScore: number,
 *   edges: object[],
 *   suppressedBy: string | null,
 *   reviewedEdgeCount: number,
 *   pendingEdgeCount: number,
 *   conditionIds: string[],
 * }>}
 */
export function remapShiftScoresToDiagnoses(conditionScores, conditionToDiagnosisId) {
  const out = {};
  for (const [conditionId, score] of Object.entries(conditionScores || {})) {
    const diagnosisId = String(conditionToDiagnosisId?.[conditionId] || '').trim();
    if (!diagnosisId) continue;

    if (!out[diagnosisId]) {
      out[diagnosisId] = {
        shiftScore: 0,
        edges: [],
        suppressedBy: null,
        reviewedEdgeCount: 0,
        pendingEdgeCount: 0,
        conditionIds: [],
      };
    }
    const target = out[diagnosisId];
    const source = /** @type {any} */ (score || {});
    target.shiftScore += Number(source.shiftScore) || 0;
    target.reviewedEdgeCount += Number(source.reviewedEdgeCount) || 0;
    target.pendingEdgeCount += Number(source.pendingEdgeCount) || 0;
    target.edges.push(...(Array.isArray(source.edges) ? source.edges : []));
    target.conditionIds.push(conditionId);
    if (source.suppressedBy) target.suppressedBy = String(source.suppressedBy);
  }

  return out;
}

/**
 * Blend existing KB score with shift score.
 * Phase 1 default: 70% KB, 30% shift.
 *
 * @param {number} kbScore
 * @param {number} shiftScore
 * @param {number} [shiftWeight]
 * @returns {number}
 */
export function blendScores(kbScore, shiftScore, shiftWeight = 0.3) {
  if (shiftScore === -999) return 0;
  const normalized = Math.max(0, Math.min(1, ((Number(shiftScore) || 0) + 10) / 20));
  const kb = Number(kbScore) || 0;
  return (kb * (1 - shiftWeight)) + (normalized * shiftWeight);
}

/**
 * Extract calculator suggestions from shift edges for top-scoring diagnoses.
 *
 * @param {Record<string, { shiftScore?: number, suppressedBy?: string | null, edges?: object[] }>} diagnosisShiftScores
 * @param {number} [topN]
 * @returns {Array<{
 *   calculatorName: string,
 *   calculatorUrl: string,
 *   triggeringFactor: string,
 *   triggeringCondition: string,
 *   inputWeight: number | null,
 * }>}
 */
export function extractCalculatorSuggestions(diagnosisShiftScores, topN = 5) {
  const suggestions = [];
  const seen = new Set();
  const topDiagnoses = Object.entries(diagnosisShiftScores || {})
    .filter(([, v]) => (Number(v?.shiftScore) || 0) > 0 && !v?.suppressedBy)
    .sort((a, b) => (Number(b[1]?.shiftScore) || 0) - (Number(a[1]?.shiftScore) || 0))
    .slice(0, topN);

  for (const [, value] of topDiagnoses) {
    for (const edge of value?.edges || []) {
      const calc = edge?.calculatorDerived;
      if (!calc?.calculatorUrl || seen.has(calc.calculatorUrl)) continue;
      seen.add(calc.calculatorUrl);
      suggestions.push({
        calculatorName: String(calc.calculatorName || ''),
        calculatorUrl: String(calc.calculatorUrl),
        triggeringFactor: String(edge.factorId || ''),
        triggeringCondition: String(edge.conditionId || ''),
        inputWeight: calc.inputWeight == null ? null : Number(calc.inputWeight),
      });
    }
  }

  return suggestions;
}

export const SHIFT_BLEND_WEIGHTS = Object.freeze({
  emergency_department: 0.3,
  primary_care: 0.4,
  urgent_care: 0.35,
});
